import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";

interface AIGenerationLoaderProps {
  tool: "script" | "product" | "keywords" | "sales-page" | "email-funnel" | "launch-plan" | "clip" | "askfint";
  className?: string;
}

const MESSAGES: Record<string, string[]> = {
  script: [
    "Analysing your niche…",
    "Crafting your hook…",
    "Writing story beats…",
    "Optimising for retention…",
    "Adding call-to-action…",
    "Polishing your script…",
  ],
  product: [
    "Researching your audience…",
    "Identifying revenue opportunities…",
    "Designing product concepts…",
    "Calculating fit scores…",
    "Building your product plan…",
  ],
  keywords: [
    "Scanning trending topics…",
    "Analysing competition levels…",
    "Finding hidden opportunities…",
    "Ranking by potential…",
  ],
  "sales-page": [
    "Studying your product…",
    "Crafting compelling headlines…",
    "Writing persuasive copy…",
    "Optimising for conversions…",
    "Adding social proof sections…",
  ],
  "email-funnel": [
    "Designing your lead magnet…",
    "Writing welcome sequence…",
    "Building nurture emails…",
    "Optimising subject lines…",
    "Structuring your funnel…",
  ],
  "launch-plan": [
    "Mapping your launch timeline…",
    "Planning content calendar…",
    "Designing promotion strategy…",
    "Setting milestone targets…",
    "Finalising your plan…",
  ],
  clip: [
    "Transcribing your video…",
    "Finding viral moments…",
    "Scoring engagement potential…",
    "Selecting top clips…",
  ],
  askfint: [
    "Reviewing your context…",
    "Researching strategies…",
    "Crafting personalised advice…",
  ],
};

const ESTIMATED_TIMES: Record<string, string> = {
  script: "~15 seconds",
  product: "~20 seconds",
  keywords: "~10 seconds",
  "sales-page": "~20 seconds",
  "email-funnel": "~20 seconds",
  "launch-plan": "~15 seconds",
  clip: "~30 seconds",
  askfint: "~5 seconds",
};

export function AIGenerationLoader({ tool, className }: AIGenerationLoaderProps) {
  const messages = MESSAGES[tool] || MESSAGES.script;
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % messages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div className={`flex flex-col items-center gap-3 py-8 ${className || ""}`}>
      <div className="relative">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <div className="absolute inset-0 w-8 h-8 rounded-full bg-primary/20 animate-ping" />
      </div>
      <p className="text-sm font-medium text-foreground animate-pulse transition-all duration-500">
        {messages[currentIdx]}
      </p>
      <p className="text-xs text-muted-foreground">
        Estimated time: {ESTIMATED_TIMES[tool]}
      </p>
    </div>
  );
}
